import './ScreenGroup.scss';
import { Container } from '@components/Component/Container/Container.js';
import DIContainer from '@components/DependencyInjectionContainer.js';
import { Subject, takeUntil } from 'rxjs';
import { debounceTime, filter, take, tap } from 'rxjs/operators';
import { createElement } from "@components/Utility/index.js";
import { Button } from "@components/Component/Button/Button.js";
/**
 * `ScreenGroup` represents a group of screens in the application.
 * It extends the `Container` class and provides methods to manage and interact with multiple screens.
 */
export class ScreenGroup extends Container {
    constructor() {
        super(...arguments);
        this.componentName = 'ScreenGroup';
        // TODO: screens map could probably just use the components object map in the Container class we are extending. 
        // unless a screengroup might have more child components besides screens maybe but even still. 
        /**
         * A map that stores screen instances, using their IDs as keys.
         */
        this.screens = new Map();
        /** The ID of the currently active screen within the screen group. */
        this.activeScreenId = '';
        this._debouncedResizeObservable = new Subject();
    }
    set screenCover(elem) {
        this._propertySet('screenCover', elem);
    }
    get screenCover() {
        return this._screenCover ?? null;
    }
    set tilesContainer(elem) {
        this._propertySet('tilesContainer', elem);
    }
    get tilesContainer() {
        return this._tilesContainer ?? null;
    }
    /**
     * Access to the containing ScreenManager.
     * @constructor
     */
    get ScreenManager() {
        if (!this.dependencies.ScreenManager) {
            // we didnt receive a request dependency injection, so just use the global singleton
            return DIContainer.getInstance('ScreenManager');
            // this.dependencies.ScreenManager = // dont set the dependency in this case because screen manager can be swapped out. 
        }
        return this.dependencies.ScreenManager;
    }
    get screenContainer() {
        return this.rootElement;
    }
    /**
     * Setup Resize observer to inform screens when the screengroup has resized.
     */
    draw() {
        super.draw();
        this.rootElement.id = this.id;
        this.screenContainer.classList.add('screen-container');
        this._debouncedResizeObservable.pipe(debounceTime(50), takeUntil(this.destroy$))
            .subscribe(() => {
            this.handleResize();
        });
        const resizeMgr = DIContainer.getInstance('ResizeManager');
        resizeMgr.observe(this.rootElement, (_element) => {
            this._debouncedResizeObservable.next();
        });
    }
    handleResize() {
    }
    /**
     * Returns the total number of screens within the screen group.
     */
    screenCountGet() {
        return this.screens.size;
    }
    /**
     * Activates the current screen group.
     */
    activateScreenGroup() {
        this.ScreenManager.activeScreenGroupSet(this.id);
    }
    /**
     * Activates the next screen in the screen group.
     */
    activateNextScreen() {
        let maxZIndex = -Infinity;
        let nextScreen;
        this.screens.forEach((screen, id) => {
            const zIndex = parseInt(window.getComputedStyle(screen.rootElement).zIndex, 10) || 0;
            if (id !== this.activeScreenId && zIndex > maxZIndex) {
                maxZIndex = zIndex;
                nextScreen = screen;
            }
        });
        if (nextScreen) {
            this.ScreenManager.activeScreenSet(nextScreen.id);
        }
        else {
            this.ScreenManager.activateNextScreen();
        }
        return nextScreen;
    }
    /**
     * Sets a given screen as the active screen within the screen group.
     * @param id - The ID of the screen to set as active.
     */
    activeScreenSet(id) {
        this.activeScreenId = id;
        // Remove active class from all screens
        this.screens.forEach(screen => {
            screen.activeSet(id === screen.id);
        });
    }
    /**
     * Sets the active state of the screen group.
     * @param b - A boolean indicating the active state of the screen group.
     */
    activeSet(b) {
        if (b) {
            this.rootElement.classList.add('active');
        }
        else {
            this.rootElement.classList.remove('active');
        }
        return this;
    }
    /**
     * Retrieves a screen from the screen group using its ID.
     * @param id - The ID of the screen to retrieve.
     */
    screenGet(id) {
        return this.screens.get(id);
    }
    /**
     * Adds a screen to the screen group.
     * @param screen - The screen instance to add to the screen group.
     */
    screenAdd(screen) {
        screen.propertySet('screenGroupId', this.id);
        this.screens.set(screen.id, screen);
        this.components[screen.id] = screen;
        this.screenContainer.appendChild(screen.rootElement);
        screen.activateScreen();
    }
    tileWindows() {
        const root = DIContainer.getInstance('Application').rootElement;
        if (root) {
            if (!this.screenCover || !this.tilesContainer) {
                this.screenCover = createElement('div', ['screen-cover']);
                root.appendChild(this.screenCover);
                root.insertAdjacentHTML('afterend', this.tilesContainerTemplate());
                this.tilesContainer = document.querySelector('.tiles-container');
                if (!this.tilesContainer) {
                    this.closeTileCover();
                    throw new Error('Tile Container not found');
                }
                const tileButtons = this.tilesContainer.querySelector('.tiles-button-container');
                const closeAllRoot = tileButtons.querySelector('[data-componentid="tiles-close-all"]');
                const arrangeAllRoot = tileButtons.querySelector('[data-componentid="tiles-arrange-all"]');
                const closeAllIcon = DIContainer.getInstance('IconManager').getIconElement('tab_close');
                const arrangeAllIcon = DIContainer.getInstance('IconManager').getIconElement('grid_view');
                let button;
                if (closeAllRoot) {
                    const closeAllBtn = new Button('tiles-close-all', closeAllRoot);
                    closeAllBtn.captionKey = 'system_close_all';
                    closeAllBtn.draw();
                    button = closeAllBtn.rootElement.querySelector('button');
                    button.classList.add('destructive');
                    button.appendChild(closeAllIcon);
                }
                if (arrangeAllRoot) {
                    const arrangeAllBtn = new Button('tiles-arrange-all', arrangeAllRoot);
                    arrangeAllBtn.captionKey = 'system_arrange_all';
                    arrangeAllBtn.draw();
                    button = arrangeAllBtn.rootElement.querySelector('button');
                    button.classList.add('button-style');
                    button.appendChild(arrangeAllIcon);
                }
                this.registerEvent('click', this.tilesContainer, this.clickDelegation.bind(this));
            }
            else {
                if (this.screenCover.isConnected) {
                    return;
                }
                root.appendChild(this.screenCover);
                root.insertAdjacentElement('afterend', this.tilesContainer);
            }
            this.renderTiles();
        }
    }
    renderTiles() {
        const screenMap = this.screens;
        const tilesGrid = this.tilesContainer?.querySelector('.tiles-grid-layout');
        screenMap.forEach((screen) => {
            if (tilesGrid) {
                tilesGrid.insertAdjacentHTML('beforeend', this.tileTemplate());
                const newTile = tilesGrid.lastChild;
                //let tile = createElement('div', ['window-tile']);
                const titleSpan = newTile.querySelector('.tile-title');
                const cloneContainer = newTile.querySelector('.tile-clone');
                const windowClone = screen.rootElement.cloneNode(true);
                windowClone.classList.remove('s360ScreenTab');
                windowClone.classList.add('s360ScreenWindow');
                if (windowClone && cloneContainer) {
                    // reset all properties that could affect styling for a consistent look
                    windowClone.style.removeProperty('top');
                    windowClone.style.removeProperty('left');
                    windowClone.style.removeProperty('height');
                    windowClone.style.removeProperty('width');
                    windowClone.classList.remove('maximized');
                    const cloneContent = windowClone.querySelector('.screen-content');
                    if (cloneContent) {
                        // force tile screen sizes to md
                        cloneContent.className = cloneContent.className.replace(/\bscreen-(xs|sm|lg|xl)\b/g, '').trim();
                        cloneContent.classList.toggle('screen-md', true);
                    }
                    cloneContainer.appendChild(windowClone);
                }
                if (titleSpan) {
                    const titleText = titleSpan.querySelector('.tile-title-text');
                    if (titleText) {
                        titleText.insertAdjacentText('afterbegin', screen.title);
                    }
                }
            }
        });
    }
    clickDelegation(event) {
        let targetFound = false;
        let target = event.target;
        while (target && !targetFound) {
            switch (true) {
                case target.getAttribute('data-componentid') === 'tiles-close-all':
                    // close all button clicked
                    targetFound = true;
                    this.closeAllScreens();
                    this.closeTileCover();
                    break;
                case target.getAttribute('data-componentid') === 'tiles-arrange-all':
                    // arrange all button clicked
                    targetFound = true;
                    this.arrangeAllWindows();
                    this.closeTileCover();
                    break;
                case target.classList.contains('tile-close-button'):
                    // close specific window
                    targetFound = true;
                    let windowID = null;
                    const windowTile = target.parentElement?.parentElement;
                    if (windowTile) {
                        const screenWindow = windowTile.querySelector('.s360ScreenWindow');
                        windowID = screenWindow?.getAttribute('data-component-id');
                    }
                    if (windowID) {
                        const screen = this.screenGet(windowID);
                        if (screen) {
                            screen.currentForm?.handleFormClose().pipe(tap(() => this.tilesContainer.classList.remove('disabled')), take(1), filter(b => b))
                                .subscribe(() => {
                                if (windowTile) {
                                    windowTile.remove();
                                }
                                if (windowID) {
                                    this.ScreenManager.screenRemove(windowID);
                                    screen.destroy();
                                }
                            });
                        }
                    }
                    break;
                case target.classList.contains('window-tile'):
                    // tile selected
                    targetFound = true;
                    const screenWindow = target.querySelector('.s360ScreenWindow');
                    const id = screenWindow?.getAttribute('data-component-id');
                    if (id) {
                        this.activeScreenSet(id);
                        this.closeTileCover();
                    }
                    break;
                default:
                    target = target.parentElement;
                    break;
            }
        }
        if (!target) {
            this.closeTileCover();
        }
    }
    closeTileCover() {
        if (this.tilesContainer) {
            const tilesGrid = this.tilesContainer?.querySelector('.tiles-grid-layout');
            tilesGrid.innerHTML = '';
            this.tilesContainer.remove();
        }
        if (this.screenCover) {
            this.screenCover.remove();
        }
    }
    arrangeAllWindows() {
        console.warn(`arrangeAllWindows() not implemented for ${this.constructor.name}`);
    }
    /**
     * Removes a screen from the screen group using its ID.
     * @param id - The ID of the screen to remove.
     */
    screenRemove(id) {
        if (id === this.activeScreenId) {
            this.activateNextScreen();
        }
        const screen = this.screenGet(id);
        if (screen) {
            this.screenContainer.removeChild(screen.rootElement);
        }
        this.screens.delete(id);
        delete this.components[id];
        if (this.screens.size === 0 && this.screenCover?.isConnected) {
            this.closeTileCover();
        }
    }
    closeAllScreens() {
        this.screens.forEach((screen) => {
            screen.closeScreen();
        });
    }
    destroy() {
        if (this.rootElement) {
            const resizeMgr = DIContainer.getInstance('ResizeManager');
            resizeMgr.unobserve(this.rootElement);
        }
        super.destroy();
    }
    /**
     * Returns the HTML template string for the ScreenGroup component.
     */
    template() {
        return '';
    }
    generateChildTemplate() {
        const template = '';
        return template;
    }
    tilesContainerTemplate() {
        return '';
    }
    tileTemplate() {
        return `<div class="window-tile">
			<div class="tile-title">
				<div class="tile-title-text"></div>
				<span class="tile-close-button">${DIContainer.getInstance('IconManager').getIconString('close')}</span>
			</div>
			<span class="tile-clone"></span>
			</div>`;
    }
}
ScreenGroup.VERSION = '$Revision: 127413 $';
